import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { translateCol } from "../../../../custom/columnI18n";
import useVisinaTabele from "../../../../custom/visinaTabele";
import usePamcenjePregleda from "../../../../custom/pamcenjePregleda";
import VozilaFilter, { PRAZAN_FILTER, primeniFilter, brojUslova } from './VozilaFilter';
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AuthContext } from "../../../../Header/auth - context";
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { FilterService } from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';

const VozilaPregled = () => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth = useContext(AuthContext);
  const company = useSelector(state => state.company);       
  const user = useSelector(state => state.user)
  const vehicle = useSelector(state => state.vehicle);
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState(null);
  /* Strana, sort, pretraga i kvacica za neaktivna se pamte i posle odlaska na
     Unos ili Dokumenta — podrazumevani prikaz (redosled sa servera, prva
     strana) vidi se samo pri prvom ulasku. */
  const [pregled, postaviPregled] = usePamcenjePregleda(
    `vozila-pregled:${company?.companyId}`,
    { first: 0, sortField: null, sortOrder: null, pretraga: '', prikaziNeaktivne: false,
      filter: PRAZAN_FILTER }
  );
  const { first, sortField, sortOrder, prikaziNeaktivne } = pregled;
  /* Filter se pamti kao i pretraga i strana — covek se vrati sa Unosa na isti
     presek. Zapamceni zapis nastao PRE uvodjenja filtera nema taj kljuc, pa bi
     bio `undefined` i citanje polja bi puklo. */
  const filter = pregled.filter || PRAZAN_FILTER;
  /* `globalFilter` promenljiva je uklonjena — pretraga se primenjuje u
     `zaPrikaz` (vidi obrazlozenje tamo), pa je tabeli vise ne prosledjujemo. */

  /* Stanje kolona stoji IZNAD `zaPrikaz` jer ga taj memo koristi — pretraga se
     primenjuje po PRIKAZANIM kolonama. Deklaracija ispod bi znacila citanje iz
     temporal dead zone i rusenje celog taba (`Cannot access 'selectedColumns'
     before initialization`), a to build ne prijavljuje. */
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]);

  /* Spisak sa servera nosi i aktivna i pasivna vozila — filtriranje je ovde,
     jer je podatak vec tu i ne treba novi poziv pri svakom prebacivanju
     kvacice. Kolona "Status" je tekst iz upita ('Aktivno' / 'Pasivno'). */
  const zaPrikaz = useMemo(() => {
    if (tableData == null) return null;
    let redovi = prikaziNeaktivne ? tableData : tableData.filter(r => r?.Status !== 'Pasivno');

    /* Uslovi filtera zive u `VozilaFilter` — i polja i pravila na jednom mestu,
       pa se prikaz i filtriranje ne mogu raziсi. Filtrira se nad vec ucitanim
       spiskom, bez novog poziva, isto kao kvacica za neaktivna iznad. */
    redovi = primeniFilter(redovi, filter);

    /* Pretraga se primenjuje OVDE, a ne kroz `globalFilter` na tabeli.
       Razlog je brojac: DataTable javlja filtriran skup samo kroz
       `onValueChange`, a taj se poziva iskljucivo iz rukovalaca (strana, sort,
       filter) — ne i pri prvom prikazu. Brojac bi zato stajao prazan dok se
       nesto ne klikne, bas kad je najpotrebniji.

       Matcher je `FilterService.filters.contains`, isti onaj koji DataTable
       interno koristi za globalFilter, pa se ponasanje pretrage ne menja.
       Pretrazuju se PRIKAZANE kolone, kao i do sada. */
    const upit = (pregled.pretraga || '').trim();
    if (upit && selectedColumns.length) {
      redovi = redovi.filter(r => selectedColumns.some(k => FilterService.filters.contains(r?.[k], upit)));
    }

    return redovi;
  }, [tableData, prikaziNeaktivne, filter, pregled.pretraga, selectedColumns]);

  /* Posto je i pretraga gore, `zaPrikaz` je tacno ono sto tabela prikazuje. */
  const brojPrikazanih = zaPrikaz == null ? null : zaPrikaz.length;
  /* "N od M" — M je spisak PRE filtera, ali uz istu kvacicu za neaktivna,
     inace bi se poredilo sa brojem koji se ionako ne prikazuje. */
  const brojUkupno = useMemo(() => {
    if (tableData == null) return 0;
    return (prikaziNeaktivne ? tableData : tableData.filter(r => r?.Status !== 'Pasivno')).length;
  }, [tableData, prikaziNeaktivne]);
  /* Brojac prelazi na "N od M" cim je spisak suzen — bilo filterom, bilo
     pretragom. Pretraga se broji jer i ona skriva redove. */
  const imaFiltera = brojUslova(filter) > 0 || !!(pregled.pretraga || '').trim();
  /* Zapamcena strana ume da ostane iza kraja spiska (vozilo je obrisano,
     kvacica za neaktivna skratila spisak) — tada se vraca na prvu, da tabela
     ne ostane prazna. */
  useEffect(() => {
    if (zaPrikaz && first > 0 && first >= zaPrikaz.length) {
      postaviPregled({ first: 0 });
    }
  }, [zaPrikaz, first, postaviPregled]);
  const getId = (row) => row?.ID ?? row?.id ?? row?.Izaberi ?? null; // ➜ stabilan ključ
  const [selectedRowId , setSelectedRowId] = useState(null);
  const isSelected = (row) => String(getId(row)) === String(selectedRowId);
  const [izabran, setIzabran] = useState(false)

  /* Brojač za ponovno čitanje spiska. Posle pasivizacije vozilo mora da nestane
     (ili dobije status Pasivno), a efekat je do sada zavisio samo od tokena i
     firme — pa se bez ovoga spisak ne bi osvežio. */
  const [osveziSpisak, setOsveziSpisak] = useState(0);

  /* Red koji je trenutno izabran — po njemu se zna da li nuditi pasivizaciju.
     Status dolazi iz procedure kao 'Aktivno' / 'Pasivno'. */
  const izabraniRed = useMemo(
    () => (tableData || []).find(r => String(getId(r)) === String(selectedRowId)) || null,
    [tableData, selectedRowId]
  );
  const izabraniJePasivan = izabraniRed?.Status === 'Pasivno';

  /* Spisak se zavrsava na dnu ekrana; visina se meri, ne pogadja.
     Zavisnost je prelom trake sa kolonama. `izabran` je tu jer izborom u
     zaglavlju nikne dugme Obrisi, pa traka ume da se prelomi u dva reda. */
  const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([zaPrikaz, selectedColumns, izabran]);
  // Path normalization and document location helper
  const getDocumentLocation = (path) => {
    if (!path) return null;
    
    // Normalize Windows path to URL format
    const normalizedPath = path.replace(/\\/g, '/');
    
    // Remove full C:/Dokumenta/ from path
    const relativePath = normalizedPath.replace(/^C:\/Dokumenta\//, '');
    const encodedPath = encodeURI(relativePath); // Encode spaces, etc.
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodedPath}`;
  };

  const handleErrors = (response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw response;
    }
  };
 

const getConfigColumns = async () => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify({
      id_config: 262
    }),
  };

    const data = await fetch(process.env.REACT_APP_BACKEND_URL + "/config/", requestOptions)
      .then(handleErrors); 

    if (data.message) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: data.message,
        life: 3000,
      });
      return { empty: true, data: [] };
    }

     if(data.length > 0) {
                    return { empty: false, data: data };
                } else {
                    return { empty: true, data: [] };
                }


};

  useEffect(() => {

    if(vehicle) {
        dispatch({
          type: "VEHICLE",
          value: null
         });
    }
    
    let requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      }
    };
    
     fetch(process.env.REACT_APP_BACKEND_URL + "/vehicles/returnAllDataVehicles", requestOptions)
      .then(handleErrors)
      .then(async (data) => {
        setTableData(data);
        
          // Extract all available columns from the first data row
            if (data && data.length > 0) {
              const columns = Object.keys(data[0])
                .filter(key => !['ID'].includes(key))
                .map(key => ({ label: key, value: key }));

              setAllColumns(columns);
              const originalOrder = Object.keys(data[0]);
              setOriginalColumnOrder(originalOrder);

              const columnsDB = await getConfigColumns();

              if (!columnsDB.empty && columnsDB.data.length > 0) {
                const arr = JSON.parse(columnsDB.data[0].parametri);
                setSelectedColumns(arr);
              } else {
                setSelectedColumns(columns.slice(0, 5).map(col => col.value));
              }
            }
      })
      .catch((error) => {
        error.json().then((errorMessage) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage.message,
            life: 3000,
          });
          console.log(errorMessage);
        });
      });
  }, [auth.token, company.companyId, osveziSpisak]);

  /* Pasivizacija (meko brisanje) stoji ovde, a ne na Unosu: Pregled i Unos su
     dva odvojena prikaza koja ne dele state, a izbor vozila živi u tabeli — pa
     je radnja nad izabranim redom na istom mestu gde se red i bira. */
  const pasivizirajVozilo = () => {
    const vozilo = izabraniRed?.['Registracija'] || '';
    confirmDialog({
      message: t('admin.potvrdaPasivizacijeVozila', { vozilo }),
      acceptLabel: t('common.da'),
      rejectLabel: t('common.ne'),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const odgovor = await fetch(process.env.REACT_APP_BACKEND_URL + "/vehicles/DeleteVehicle", {
            method: "DELETE",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ id: selectedRowId }),
          });
          if (!odgovor.ok) throw odgovor;

          /* Telo ume da bude prazno (radnja je prošla, nema šta da se vrati), a
             odgovor.json() nad praznim telom baca grešku — pa se prvo čita tekst. */
          const telo = await odgovor.text();
          const podaci = telo ? JSON.parse(telo) : [];
          if (podaci?.[0]?.message) {
            toast.current.show({ severity: "warn", summary: t('admin.m_upozorenje_2'),
                                 detail: podaci[0].message, life: 4000 });
            return;
          }

          setSelectedRowId(null);
          setIzabran(false);
          dispatch({ type: "VEHICLE", value: null });
          setOsveziSpisak(k => k + 1);

          toast.current.show({ severity: "success",
                               summary: t('admin.m_uspesno_pasiviziranje_podataka_vozila'), life: 3000 });
        } catch (e) {
          let poruka = t('common.greska');
          try { const j = await e.json(); if (j?.message) poruka = j.message; } catch { /* telo nije JSON */ }
          toast.current.show({ severity: "error", summary: t('common.greska'), detail: poruka, life: 5000 });
        }
      },
    });
  };

  const chooseVehicle = (rowData) => {
    const id = getId(rowData);

    setSelectedRowId((prev) => {
      const next = String(prev) === String(id) ? null : String(id);

    if (next) {
      // selekcija
      const vehicleID = tableData?.find(t => String(t.Izaberi) === String(rowData.Izaberi));
      if (vehicleID) {
    
        setIzabran(true);
      }
      dispatch({ type: "VEHICLE", value: rowData.Izaberi });
    } else {
      // deselekcija
      setIzabran(false);
      dispatch({ type: "VEHICLE", value: null });
    }

    return next;
  });
  }

  const previewBodyTemplate = (rowData, field) => {
   const filePathsString = rowData[field];

   if (!filePathsString) return '-';

   if(field == 'Izaberi') {

      isSelected(rowData);
       return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Button
                    label='Izaberi'
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm"
                    onClick={(e) => chooseVehicle(rowData)}
                    tooltip="Izaberi putni nalog za dalji rad"
                    tooltipOptions={{ position: 'top' }}
                  />
                
            </div>
          );
   } else {
   const filePaths = filePathsString.split('|').filter(p => p.trim() !== '');
 
   return (
     <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
       {filePaths.map((path, idx) => {
         const documentUrl = getDocumentLocation(path);
         if (!documentUrl) return null;
 
         // Izdvoji ime fajla bez ekstenzije
         const fileNameWithExt = path.split('/').pop(); // npr. TOI-Psihologija-2016-testkljuc_20250726222909703.pdf
         const fileName = fileNameWithExt.replace(/\.(pdf|jpg|png)$/i, '');
 
         return (
           <Button
             key={idx}
             label={fileName}
             icon="pi pi-eye"
             className="p-button-text p-button-sm"
             onClick={() => window.open(documentUrl, '_blank')}
             tooltip="Pregledaj dokument"
             tooltipOptions={{ position: 'top' }}
           />
         );
       })}
     </div>
   );
   }  
 };

  //PROMENITI KAD SE SREDI UNOS
  const isFileColumn = (field) => {
    return field.startsWith('Fajl') || 
           field.includes('File') || 
           field.includes('Izaberi') || 
           field.includes('upload') ||
           field.endsWith('LK') || // Lična karta
           field.endsWith('VD') || // Vozačka dozvola
           field.endsWith('PASS') || // Pasoš
           field.endsWith('VIZA') || // Viza
           field.includes('Dokument'); // Generic document
  };

  const exportExcel = async () => {
      if (!selectedColumns || selectedColumns.length === 0) return;
  
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Vozila');
  
      // Use selectedColumns instead of all table keys
      const headers = selectedColumns.map(c => translateCol(t, c));
  
      // Add header row
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.eachCell((cell) => {
          cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFEFEFEF' }
          };
          cell.border = {
              top: { style: 'medium' },
              left: { style: 'medium' },
              bottom: { style: 'medium' },
              right: { style: 'medium' }
          };
      });
  
      // Add filtered data rows
      (zaPrikaz || []).forEach((rowData) => {
          const row = worksheet.addRow(headers.map(h => rowData[h] ?? ""));
          row.eachCell((cell) => {
              cell.border = {
                  top: { style: 'thin' },
                  left: { style: 'thin' },
                  bottom: { style: 'thin' },
                  right: { style: 'thin' }
              };
          });
      });
  
      // Auto-size columns
      worksheet.columns.forEach((column, i) => {
          const lengths = [
              headers[i].length,
              ...(zaPrikaz || []).map(row => (row[headers[i]] ?? "").toString().length)
          ];
          column.width = Math.max(...lengths) + 2;
      });
  
      // Serbian-formatted datetime
      const date = new Date();
      const options = {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Belgrade'
      };
      const serbianDateTime = date.toLocaleString('sr-RS', options)
          .replace(/\s/g, ' ')
          .replace(/,/g, '');
  
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `Vozila - ${company.companyName} - ${serbianDateTime}.xlsx`);
  };
       const header = (
          <div className="export-buttons fakture-stavke-header">
          <div className="fakture-stavke-radnje">
              <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
              {/* Broj vozila — prikazuje se ono STO SE VIDI u tabeli, a kad je
                  spisak suzen i koliko ih je ukupno, da se zna sta filter krije. */}
              <span className="pregled-brojac">
                {brojPrikazanih == null ? '' :
                  imaFiltera
                    ? t('admin.brojVozilaOd', { prikazano: brojPrikazanih, ukupno: brojUkupno })
                    : t('admin.brojVozila', { broj: brojPrikazanih })}
              </span>

              {/* NAPREDNI FILTER — polja su u popoveru, ne u zaglavlju: pet
                  padajucih lista ovde je pojelo ceo red i prelomilo pretragu.
                  Isti obrazac kao kod pregleda zaposlenih, da se dva pregleda
                  ne razilaze. */}
              <VozilaFilter
                  sviRedovi={tableData}
                  filter={filter}
                  postaviFilter={(novi) => postaviPregled({ filter: novi, first: 0 })}
              />

              {/* Pasivizacija izabranog vozila. Stoji samo za AKTIVNO — pasivno
                  nema smisla pasivizirati ponovo. */}
              {izabran && !izabraniJePasivan && (
                <Button type="button" icon="pi pi-trash" severity="danger" rounded
                        onClick={pasivizirajVozilo} data-pr-tooltip={t('common.delete')} />
              )}
          </div>
          <input
               type="search"
               defaultValue={pregled.pretraga || ''}
               onInput={(e) => postaviPregled({ pretraga: e.target.value, first: 0 })}
               placeholder={t('nova.phPretraga')}
               className="p-inputtext p-component"
               />
      </div>
      );
      
  const handleSaveConfig = () => {
         confirmDialog({
                    message: "Jel ste sigurni da želite sačuvati konfiguraciju?",
                    acceptLabel: "Da",
                    rejectLabel: "Ne",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: acceptInsertConfig,
                    reject: rejectInsertConfig,
                    });
  }

  const acceptInsertConfig  = () => {
   if(selectedColumns.length > 0) {
      let requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      },
      body: JSON.stringify({
        id_config: 262,
        parametri: selectedColumns
      }),
    };
    
    fetch(process.env.REACT_APP_BACKEND_URL + "/config/changeConfig", requestOptions)
      .then(handleErrors)
      .then((data) => {

            if(data.message && data.message == 'Nema izmena u podacima konfiguracije pregleda.'){
                toast.current.show({
                    severity: "warn",
                    summary: t('admin.m_upozorenje_2'),
                    detail: data.message,
                    life: 3000,
                    });
                return
                }  
  
            toast.current.show({
                severity: "success",
                summary: t('admin.m_uspesna_izmena_konfiguracije_pregleda_za_3'),
                life: 3000,
            });

      })
      .catch((error) => {
        error.json().then((errorMessage) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage.message,
            life: 3000,
          });
          console.log(errorMessage);
        });
      }); 
    } else {
        toast.current.show({
                    severity: "warn",
                    summary: t('admin.m_upozorenje_2'),
                    detail: t('admin.m_morate_imati_barem_1_izabranu_stavku'),
                    life: 3000,
                    });
    }
  }
  const rejectInsertConfig = () => {
    console.log('Config insert rejected!')
  }
  
  return (
    <div className="zaposleni_table_container">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1em" }}>
                <div style={{ width: "20%" }}>
                  {allColumns.length > 0 && (
                    <div style={{display:'flex', alignItems:'center', justifyContent:'center'}}>  <MultiSelect
                      value={selectedColumns}
                      options={allColumns}
                      onChange={(e) => {
          const sorted = originalColumnOrder.filter((key) => e.value.includes(key));
          setSelectedColumns(sorted);
        }}
                      optionLabel="label"
                      display="chip"
                      placeholder={t('nova.odaberiteKolone')}
                      style={{ width: '100%' }}
                      showSelectAll={true}
                      filter
                      filterPlaceholder="Pretraži kolone"
                      maxSelectedLabels={3}
                      selectedItemsLabel="{0} kolona odabrano"
                    />
                    <button onClick={handleSaveConfig} style={{marginLeft:'1%'}} className="configSaveButton"><i className="pi pi-save"></i></button>
                    {/* Podrazumevano se vide samo aktivna vozila; kvacica dodaje i pasivna. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: '0.8rem', whiteSpace: 'nowrap' }}>
                      <Checkbox inputId="prikaziNeaktivne" checked={prikaziNeaktivne}
                                onChange={(e) => postaviPregled({ prikaziNeaktivne: e.checked, first: 0 })} />
                      <label htmlFor="prikaziNeaktivne" style={{ cursor: 'pointer', margin: 0 }}>{t('admin.neaktivni')}</label>
                    </div></div>
                  
                  )}
                </div>
      </div>
      {/* Dugmad su u .fakture-stavke-radnje, dakle UNUCI .export-buttons —
          `>` je direktan potomak, pa ih stari cilj nije hvatao i `data-pr-tooltip`
          (XLS, Obriši) nije radio. */}
      <Tooltip target=".fakture-stavke-radnje>button" position="bottom" />
      <Tooltip target=".export-buttons>button" position="bottom" />
      <div ref={omotTabele}>
      <DataTable className="stavke-tabela"
        value={zaPrikaz}
        paginator
        rows={redovaTabele}
        header={header}
        first={first}
        /* Bez `globalFilter` — pretraga je primenjena u `zaPrikaz`, da bi brojac
           mogao da bude tacan i pri prvom prikazu. */
        onPage={(e) => postaviPregled({ first: e.first })}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => postaviPregled({ sortField: e.sortField, sortOrder: e.sortOrder, first: 0 })}
        scrollable
        scrollHeight={visinaTabele}
        emptyMessage={t('common.noDataTable')}
        loading={zaPrikaz === null}
        resizableColumns
        columnResizeMode="expand"
        /* Pasivno vozilo se izdvaja crvenim redom; izbor reda ima prednost. */
        rowClassName={(data) =>
          isSelected(data)
            ? "selected-row"
            : (data?.Status === "Pasivno" ? "row-invoice-overdue" : "")
        }
      >
         {selectedColumns.map((colKey) => {
               const colMeta = allColumns.find(col => col.value === colKey);
               return (
                 <Column
                   key={colKey}
                   field={colKey}
                   header={translateCol(t, colMeta?.label || colKey)}
                   sortable
                   body={(rowData) => {
                     if (isFileColumn(colKey)) {
                       return previewBodyTemplate(rowData, colKey);
                     }
                     return rowData[colKey] || '-';
                   }}
                   style={{ minWidth: '150px'}}
                 />
               );
             })}
      </DataTable>
      </div>
    </div>
  );
};

export default VozilaPregled;